// apps/client/src/game/interactions/yardTidy.ts (M1-T16, Contract change 29; spec §4.4 "Furniture placed outdoors can be removed only
// by its owner or the mayor", D-39). Decorate mode only works in your own yard, and M1-T15's `remove` only covers MAYOR_REMOVABLE world
// objects, so this is the mayor's only way to reach M1-T11's tidy-up: another resident's placed yard piece, empty hands, outdoors, at
// the lowest priority. A never acts directly: it asks first (confirmTidy).
import { getEntry, t, tuning, type StringKey } from '@whiskerton/content';
import type { ItemId, ItemView, Rot } from '@whiskerton/shared';
import { footprintOf } from '@whiskerton/shared/rules/placement';
import { itemsLayerOf } from '../../world/items';
import type { Interaction } from '../interact';

type YardPiece = ItemView & { at: { loc: 'outdoors'; x: number; y: number; rot: Rot; placed: true } };
const isYardPiece = (i: ItemView): i is YardPiece => i.at.loc === 'outdoors' && i.at.placed;

/** In front of you (the cosine off your facing), as for remove.ts: the M1 playtest saw Remove offered for a piece behind you. */
const FRONT_COS = 0.2;

export const interaction: Interaction = {
  id: 'yardTidy',
  find(ctx) {
    const me = ctx.store.you?.resident;
    if (!me || ctx.store.residents[me]?.role !== 'mayor' || ctx.space !== 'out' || ctx.tool !== null) return null;
    let best: { id: ItemId; d: number; at: { x: number; y: number } } | null = null;
    for (const i of itemsLayerOf(ctx.game)?.items() ?? []) {
      if (!isYardPiece(i) || i.owner === null || i.owner === me) continue;       // own pieces: decorate mode
      const { w, d } = footprintOf({ catalogId: i.catalogId, rot: i.at.rot });
      const nx = Math.min(Math.max(ctx.pos.x, i.at.x), i.at.x + w), ny = Math.min(Math.max(ctx.pos.y, i.at.y), i.at.y + d);
      const dist = Math.hypot(nx - ctx.pos.x, ny - ctx.pos.y);                    // to the nearest point of the footprint
      if (dist > tuning.town.interactRange || (best && dist >= best.d)) continue;
      if (dist > 1e-6 && ((nx - ctx.pos.x) * Math.sin(ctx.f) + (ny - ctx.pos.y) * Math.cos(ctx.f)) / dist < FRONT_COS) continue;   // in front
      best = { id: i.id, d: dist, at: { x: i.at.x + w / 2, y: i.at.y + d / 2 } };
    }
    return best && { id: best.id, at: best.at, verb: 'hud.verbRemove', priority: 1 };  // lowest, like M1-T15's remove
  },
  run(ctx, c) {                                                                   // never acts directly: confirm first
    const i = itemsLayerOf(ctx.game)?.items().find((x) => x.id === c.id);
    if (!i || !i.owner) return;
    ctx.store.openScreen('confirmTidy', { item: i.id, name: (getEntry(i.catalogId)?.name ?? 'common.notice') as StringKey,
      owner: ctx.store.residents[i.owner]?.name ?? t('common.formerResident') });
  },
};
